import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { checkBankrollMismatch, getMismatchAlertMessage } from '../utils/bankrollUtils';
import { useBankrollMismatch } from '../contexts/BankrollMismatchContext';
import axios from 'axios';

function BankrollMismatchAlert() {
    const [mismatchData, setMismatchData] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const { shouldCheckMismatch } = useBankrollMismatch();

    const checkMismatch = async () => {
        try {
            // Fetch both bankroll and storage data
            const [bankrollResponse, storageResponse] = await Promise.all([
                axios.get('/api/bankroll/'),
                axios.get('/api/bankroll-storage/')
            ]);

            const calculatedBankroll = bankrollResponse.data?.results?.[0]?.current_balance || 
                                     bankrollResponse.data?.[0]?.current_balance || 0;
            const storageLocations = storageResponse.data?.results || storageResponse.data || [];

            const mismatch = checkBankrollMismatch(calculatedBankroll, storageLocations);
            
            if (mismatch.isMismatch) {
                setMismatchData(mismatch);
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        } catch (error) {
            console.error('Error checking bankroll mismatch:', error);
        }
    };

    useEffect(() => {
        // Check for mismatch on component mount
        checkMismatch();

        // Set up periodic checking every 30 seconds
        const interval = setInterval(checkMismatch, 30000);

        return () => clearInterval(interval);
    }, [shouldCheckMismatch]); // Re-run when context triggers a check

    // Add/remove top margin to main content when alert is visible
    useEffect(() => {
        const mainElement = document.querySelector('main');
        if (mainElement) {
            if (isVisible && !isDismissed && mismatchData) {
                mainElement.style.marginTop = '4rem'; // 64px to account for alert height
            } else {
                mainElement.style.marginTop = '0';
            }
        }

        return () => {
            if (mainElement) {
                mainElement.style.marginTop = '0';
            }
        };
    }, [isVisible, isDismissed, mismatchData]);

    const handleDismiss = () => {
        setIsDismissed(true);
        setIsVisible(false);
        
        // Reset dismissal after 1 hour
        setTimeout(() => {
            setIsDismissed(false);
        }, 3600000);
    };

    const handleRefresh = () => {
        setIsDismissed(false);
        checkMismatch();
    };

    if (!isVisible || isDismissed || !mismatchData) {
        return null;
    }

    return (
        <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-4 py-3 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex-1">
                    <p className="text-sm sm:text-base font-medium">
                        {getMismatchAlertMessage(mismatchData)}
                    </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                    <button
                        onClick={handleRefresh}
                        className="px-3 py-1 bg-red-700 hover:bg-red-800 rounded text-xs sm:text-sm font-medium transition-colors"
                    >
                        Refresh
                    </button>
                    <button
                        onClick={handleDismiss}
                        className="p-1 hover:bg-red-700 rounded transition-colors"
                        title="Dismiss for 1 hour"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BankrollMismatchAlert; 